import './styles/app.css';
import { route, setNotFound, startRouter, navigate } from './lib/router.js';
import { api } from './lib/api.js';
import { renderLogin } from './pages/login.js';
import { renderPublicForm } from './pages/public-form.js';
import { renderDashboard, renderBusinessDetail, renderFormCustomize } from './pages/dashboard.js';
import { renderPlatform } from './pages/platform.js';
import { centered } from './lib/shell.js';
import { isPlatformAdmin } from './lib/auth.js';
import { isEmbedded } from './lib/embed.js';
import { trackFrameHeight } from './lib/frame-height.js';

const app = document.getElementById('app')!;

// Embed only, and once for the whole session rather than per route: the shell is
// rebuilt on every navigation, so --frame-h lives on documentElement and the
// observer has to outlive the element it sizes.
if (isEmbedded()) trackFrameHeight();

/**
 * Post-login landing. Platform admins (not currently impersonating an agency)
 * belong in the platform console; everyone else in the agency dashboard. This
 * is the redirect target of the auth flow, so the choice happens once here.
 */
async function landing(): Promise<void> {
  type MeLite = { authenticated: boolean; user?: { realmRoles?: string[] }; actingAgency?: unknown };
  let me: MeLite;
  try { me = await api.get<MeLite>('/auth/me'); }
  catch { me = { authenticated: false }; }
  if (!me.authenticated) { navigate('/login'); return; }
  // Inside the GHL frame the destination is not a choice: the frame was opened
  // from a sub-account's menu, so the agency dashboard is what it is for. A
  // platform admin who lands here gets that view rather than the platform
  // console, which has no meaning scoped to one sub-account.
  if (isEmbedded()) { navigate('/app/overview'); return; }
  if (isPlatformAdmin(me.user?.realmRoles) && !me.actingAgency) { navigate('/platform'); return; }
  navigate('/app/overview');
}

// Public, unauthenticated
route('/f/:token', (root, p) => renderPublicForm(root, p.token));
route('/login', (root) => renderLogin(root));

// Authenticated agency dashboard
route('/', () => landing());
// The GHL iframe entry. The server has already recorded the sub-account from the
// location_id merge field by the time this renders; see api/embed.go.
route('/embed', () => landing());
route('/app', () => navigate('/app/overview'));
route('/app/businesses/:id', (root, p) => renderBusinessDetail(root, p.id));
route('/app/forms/:id/customize', (root, p) => renderFormCustomize(root, p.id));
route('/app/:sub', (root, p) => renderDashboard(root, p.sub));

// Platform admin
route('/platform', (root) => renderPlatform(root));

setNotFound((root) => {
  root.innerHTML = centered('Page not found.');
});

// Global: sign out
document.addEventListener('click', async (e) => {
  const btn = (e.target as HTMLElement).closest('[data-action="logout"]');
  if (!btn) return;
  e.preventDefault();
  try { await api.post('/auth/logout'); } catch { /* ignore */ }
  navigate('/login');
});

startRouter(app);
